import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Eye, LockSimple } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const buildUrl = (path) => (path?.startsWith('http') ? path : `${BACKEND_URL}${path}`);

/**
 * Point 63 — placeholder shown in place of a view-once photo/video once the
 * backend has stripped its URL (viewer isn't the sender). Tapping calls the
 * one-time "open" endpoint; the reveal is shown inline for this render only
 * — reloading the chat will show "already viewed" since the server has
 * already recorded the open.
 */
const ViewOnceBubble = ({ msg, openUrl, userId }) => {
  const [revealed, setRevealed] = useState(null); // { image_url, voice_url } once opened
  const [loading, setLoading] = useState(false);
  const alreadyOpened = msg.view_once_state === 'opened';

  const open = async () => {
    if (alreadyOpened || loading) return;
    setLoading(true);
    try {
      const res = await axios.post(openUrl, { user_id: userId });
      setRevealed(res.data);
    } catch (e) {
      if (e?.response?.status === 410) {
        toast.error('This media is no longer available');
      } else {
        toast.error('Failed to open');
      }
    } finally {
      setLoading(false);
    }
  };

  if (revealed) {
    return (
      <div className="rounded-lg overflow-hidden -mx-2 -mt-1.5 mb-1" data-testid="view-once-revealed">
        {revealed.voice_url ? (
          <audio src={buildUrl(revealed.voice_url)} controls className="w-full" />
        ) : (
          <img src={buildUrl(revealed.image_url)} alt="View once" className="w-full max-w-[280px] max-h-[320px] object-cover block" />
        )}
        <div className="px-2 py-1 text-[10px] text-gray-400 bg-gray-900/5">Opened — won't be available again</div>
      </div>
    );
  }

  return (
    <button
      onClick={open}
      disabled={alreadyOpened || loading}
      className={`flex items-center gap-2 px-3 py-2.5 rounded-lg w-full text-left ${alreadyOpened ? 'bg-gray-100 text-gray-400' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`}
      data-testid="view-once-placeholder"
    >
      {alreadyOpened ? <LockSimple size={18} /> : <Eye size={18} weight="fill" />}
      <span className="text-sm font-medium">
        {loading ? 'Opening…' : alreadyOpened ? 'Opened — no longer available' : 'Tap to view once'}
      </span>
    </button>
  );
};

export default ViewOnceBubble;
